import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PageHero } from '@/components/shared/PageHero';
import { ANNOUNCEMENTS } from '@/data/mockData';
import { formatDate } from '@/lib/storage';
import { ArrowLeft, Calendar, Pin, Share2, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const AnnouncementDetail = () => {
  const { id } = useParams();
  const item = ANNOUNCEMENTS.find((a) => a.id === id);

  if (!item) return <Navigate to="/announcements" replace />;

  const others = ANNOUNCEMENTS.filter((a) => a.id !== id).slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: item.title, url }); } catch {}
    } else {
      navigator.clipboard.writeText(url);
      toast.success('Link copied to clipboard');
    }
  };

  return (
    <div data-testid="announcement-detail-page">
      <PageHero
        eyebrow={item.category}
        title={item.title}
        breadcrumbs={[{ label: 'Announcements', to: '/announcements' }, { label: 'Details' }]}
      >
        <div className="flex items-center gap-3 text-sm">
          <Calendar className="h-4 w-4" />
          <span>{formatDate(item.date)}</span>
          {item.pinned && <Badge className="bg-warning/30 text-white hover:bg-warning/40 border-0"><Pin className="h-3 w-3 mr-1" /> Pinned</Badge>}
        </div>
      </PageHero>

      <section className="container-app section-padding">
        <div className="grid lg:grid-cols-3 gap-10">
          <article className="lg:col-span-2">
            <Card className="overflow-hidden border-0 shadow-md-soft">
              {item.image ? (
                <div className="aspect-[16/9] bg-secondary overflow-hidden">
                  <img src={item.image} alt={item.title} className="h-full w-full object-cover" />
                </div>
              ) : (
                <div className="aspect-[16/9] bg-gradient-to-br from-primary/15 to-accent-soft flex items-center justify-center">
                  <Building2 className="h-14 w-14 text-primary/40" />
                </div>
              )}
              <div className="p-8">
                <p className="font-display text-lg/relaxed text-foreground/85">{item.summary}</p>
                <div className="mt-6 prose prose-slate max-w-none text-foreground/80 leading-relaxed">
                  <p>{item.content}</p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <Button asChild variant="outline" data-testid="announcement-back-cta">
                    <Link to="/announcements"><ArrowLeft className="mr-2 h-4 w-4" /> Back to all</Link>
                  </Button>
                  <Button onClick={handleShare} className="bg-gradient-primary text-primary-foreground" data-testid="announcement-share-cta">
                    <Share2 className="mr-2 h-4 w-4" /> Share
                  </Button>
                </div>
              </div>
            </Card>
          </article>

          <aside>
            <h3 className="font-heading font-semibold mb-4">More announcements</h3>
            <div className="grid gap-4">
              {others.map((o) => (
                <Link key={o.id} to={`/announcements/${o.id}`} className="block group">
                  <Card className="p-4 card-elevated border-0">
                    <Badge variant="secondary" className="bg-primary/10 text-primary text-[10px]">{o.category}</Badge>
                    <h4 className="mt-2 text-sm font-semibold leading-snug line-clamp-2 group-hover:text-primary transition">{o.title}</h4>
                    <div className="mt-2 text-xs text-muted-foreground">{formatDate(o.date)}</div>
                  </Card>
                </Link>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default AnnouncementDetail;
